import React, { useState } from 'react';
import { Button, ProgressBar, Alert } from './ui';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import groupService from '../services/groupService';

const BatchUploadModal = ({ isOpen, onClose, links, onComplete }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const BATCH_SIZE = 1000; // Process 1000 links at a time
  const MAX_CONCURRENT = 1; // Sequential processing to avoid server overload
  const MAX_RETRIES = 3; // Max retries for failed requests
  const RETRY_DELAY = 2000; // Base delay between retries (2 seconds)

  // Function to process a single batch with retry logic
  const processBatchWithRetry = async (batch, batchNumber, totalBatches) => {
    let lastError = null;
    
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`Processing batch ${batchNumber}/${totalBatches} with ${batch.length} links (attempt ${attempt}/${MAX_RETRIES})`);
        
        const result = await groupService.addBulkGroups({ links: batch });
        
        // Success - update results
        setResults(prev => [...prev, {
          batchIndex: batchNumber,
          status: 'success',
          message: `Batch ${batchNumber}: ${result.added} added, ${result.skipped_existing} existing, ${result.invalid} invalid`,
          details: result
        }]);

        return result;
        
      } catch (err) {
        lastError = err;
        console.error(`Error in batch ${batchNumber} (attempt ${attempt}):`, err);
        
        // Check if it's a 503 Service Unavailable or network error that we should retry
        const shouldRetry = (
          (err.response && err.response.status === 503) || // Service Unavailable
          (err.response && err.response.status === 502) || // Bad Gateway
          (err.code === 'NETWORK_ERROR') || // Network issues
          (err.message && err.message.includes('timeout')) // Timeout errors
        ) && attempt < MAX_RETRIES;
        
        if (shouldRetry) {
          // Exponential backoff: 2s, 4s, 8s
          const delay = RETRY_DELAY * Math.pow(2, attempt - 1);
          console.log(`Retrying batch ${batchNumber} in ${delay}ms...`);
          
          // Update UI to show retry
          setResults(prev => [...prev, {
            batchIndex: batchNumber,
            status: 'warning',
            message: `Batch ${batchNumber}: Retry ${attempt}/${MAX_RETRIES} in ${delay/1000}s (${err.response?.status || err.message})`,
            details: null
          }]);
          
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          // Final failure - no more retries
          break;
        }
      }
    }
    
    // All retries failed
    setResults(prev => [...prev, {
      batchIndex: batchNumber,
      status: 'error',
      message: `Batch ${batchNumber}: Failed after ${MAX_RETRIES} attempts - ${lastError.response?.status || lastError.message}`,
      details: null
    }]);

    return { added: 0, skipped_existing: 0, invalid: batch.length, error: lastError.message };
  };

  const processBatches = async () => {
    if (!links || links.length === 0) return;

    setIsProcessing(true);
    setError(null);
    setResults([]);
    setSummary(null);

    try {
      // Split links into batches
      const batches = [];
      for (let i = 0; i < links.length; i += BATCH_SIZE) {
        batches.push(links.slice(i, i + BATCH_SIZE));
      }

      setProgress({ current: 0, total: batches.length, percentage: 0 });

      const allResults = [];
      let totalAdded = 0;
      let totalExisting = 0;
      let totalInvalid = 0;

      // Process batches with limited concurrency
      for (let i = 0; i < batches.length; i += MAX_CONCURRENT) {
        const batchGroup = batches.slice(i, i + MAX_CONCURRENT);
        
        const promises = batchGroup.map(async (batch, batchIndex) => {
          const actualBatchIndex = i + batchIndex;
          return await processBatchWithRetry(batch, actualBatchIndex + 1, batches.length);
        });

        const batchResults = await Promise.all(promises);
        
        // Accumulate results
        batchResults.forEach(result => {
          if (result) {
            totalAdded += result.added || 0;
            totalExisting += result.skipped_existing || 0;
            totalInvalid += result.invalid || 0;
          }
        });

        allResults.push(...batchResults);

        // Update progress
        const completedBatches = Math.min(i + MAX_CONCURRENT, batches.length);
        setProgress({
          current: completedBatches,
          total: batches.length,
          percentage: Math.round((completedBatches / batches.length) * 100)
        });

        // Small delay between batch groups to be nice to the server
        if (i + MAX_CONCURRENT < batches.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Set final summary
      setSummary({
        totalProcessed: links.length,
        totalAdded,
        totalExisting,
        totalInvalid,
        batchesProcessed: batches.length,
        successfulBatches: allResults.filter(r => !r.error).length,
        failedBatches: allResults.filter(r => r.error).length
      });

      // Call completion callback
      if (onComplete) {
        onComplete({
          success: true,
          totalAdded,
          totalExisting,
          totalInvalid,
          totalProcessed: links.length
        });
      }

    } catch (err) {
      console.error('Error in batch processing:', err);
      setError(`Batch processing failed: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
      // Reset state
      setProgress({ current: 0, total: 0, percentage: 0 });
      setResults([]);
      setSummary(null);
      setError(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      ></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Batch Upload Groups
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Processing <strong>{links?.length || 0}</strong> links in batches of <strong>{BATCH_SIZE}</strong>
              </p>
              
              {progress.total > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Progress: {progress.current}/{progress.total} batches</span>
                    <span>{progress.percentage}%</span>
                  </div>
                  <ProgressBar value={progress.percentage} />
                </div>
              )}
            </div>

            {error && (
              <Alert variant="error" className="mb-4">
                {error}
              </Alert>
            )}

            {summary && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Final Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p><strong>Total Processed:</strong> {summary.totalProcessed.toLocaleString()}</p>
                    <p className="text-green-600"><strong>Added:</strong> {summary.totalAdded.toLocaleString()}</p>
                    <p className="text-blue-600"><strong>Already Existed:</strong> {summary.totalExisting.toLocaleString()}</p>
                    <p className="text-red-600"><strong>Invalid:</strong> {summary.totalInvalid.toLocaleString()}</p>
                  </div>
                  <div>
                    <p><strong>Batches Processed:</strong> {summary.batchesProcessed}</p>
                    <p className="text-green-600"><strong>Successful:</strong> {summary.successfulBatches}</p>
                    <p className="text-red-600"><strong>Failed:</strong> {summary.failedBatches}</p>
                  </div>
                </div>
              </div>
            )}

            {results.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Batch Results</h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start space-x-2 p-2 bg-gray-50 rounded text-sm">
                      {getStatusIcon(result.status)}
                      <span className="flex-1">{result.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              {!isProcessing && !summary && (
                <>
                  <Button variant="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={processBatches}
                    disabled={!links || links.length === 0}
                  >
                    Start Batch Upload
                  </Button>
                </>
              )}
              
              {isProcessing && (
                <Button variant="secondary" disabled>
                  Processing... ({progress.percentage}%)
                </Button>
              )}
              
              {summary && !isProcessing && (
                <Button variant="primary" onClick={handleClose}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchUploadModal;
